import { AlertCircle, X } from "lucide-react";
import type React from "react";

interface ModalProps {
	isOpen: boolean;
	onClose: () => void;
	children: React.ReactNode;
	title?: string;
	onClick?: (e: React.MouseEvent) => void;
}

export default function Modal({ isOpen, onClick, onClose, title, children }: ModalProps) {
	if (!isOpen) return null;

	const handleBackdropClick = (e: React.MouseEvent) => {
		if (e.target === e.currentTarget) {
			onClose();
		}
	};

	return (
		<div
			className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
			onClick={handleBackdropClick}
			onKeyDown={(e) => {
				if (e.key === "Escape") {
					onClose();
				}
			}}
		>
			<div className="bg-white rounded-2xl shadow-2xl max-w-md w-full">
				{/* Close button */}
				<div className="flex justify-end p-4">
					<button
						type="button"
						onClick={onClick}
						className="p-1 hover:bg-gray-100 rounded-full transition-colors"
						aria-label="Close modal"
					>
						<X className="w-5 h-5 text-gray-400" />
					</button>
				</div>

				{/* Modal content */}
				<div className="px-8 pb-8 pt-0 text-center">
					{/* Icon */}
					<div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
						<AlertCircle className="w-8 h-8 text-red-500" onClick={onClose} />
					</div>

					{/* Title */}
					{title && <h2 className="text-2xl font-bold text-gray-900 mb-4">{title}</h2>}

					{/* Content */}
					<div className="mb-8">{children}</div>

					{/* Button */}
					<button
						type="button"
						onClick={onClick}
						className="w-full bg-gradient-to-r from-orange-400 to-pink-400 hover:from-orange-500 hover:to-pink-500 text-white font-semibold py-4 px-8 rounded-full text-lg transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-xl"
					>
						Try again
					</button>
				</div>
			</div>
		</div>
	);
}
